import { useCallback, useMemo } from 'react';
import { useForm as useRHFForm, UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { emailValidation } from '../../../core/formValidation';
import { getApiStatus } from '../../../core/utils';
import { statusSelector } from '../../../store/authReducer';
import { forgotPassword, FORGOT_PASSWORD_REQUEST, statusError, statusReset } from '../actions';

export interface FormValues {
  username: string;
}

// Formik-like type so the component API remains unchanged
type FormikFacade = {
  getFieldProps: (name: keyof FormValues | string) => {
    name: string;
    value: any;
    onChange: (e: any) => void;
    onBlur: (e: any) => void;
  };
  isSubmitting: boolean;
};

interface Form {
  formik: FormikFacade;
  handleSubmitClick: () => void;
  statusMessage: { type: string; message: string };
  removeErrorMessage: () => void;
}

const useForgotPasswordForm = (): Form => {
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => getApiStatus(state, FORGOT_PASSWORD_REQUEST)) as boolean;
  const statusMessage = useSelector((state) => statusSelector(state));

  const rhf: UseFormReturn<FormValues> = useRHFForm<FormValues>({
    mode: 'onChange',
    defaultValues: { username: '' },
  });

  const onSubmit = useCallback(
    (values: FormValues) => {
      if (emailValidation(values.username)) {
        dispatch(forgotPassword(values.username));
      } else {
        dispatch(statusError('invalidEmail'));
      }
    },
    [dispatch]
  );

  // watch all fields so facade recomputes when values change
  const watched = rhf.watch();

  const formik: FormikFacade = useMemo(() => {
    const getFieldProps = (name: keyof FormValues | string) => {
      const value = (rhf.getValues() as any)[name];
      const onChange = (e: any) => {
        const next = e?.target?.value ?? e;
        rhf.setValue(name as any, next, { shouldValidate: true, shouldDirty: true });
      };
      const onBlur = () => rhf.trigger(name as any);
      return { name: String(name), value, onChange, onBlur };
    };

    return {
      getFieldProps,
      isSubmitting: rhf.formState.isSubmitting || !!isLoading,
    };
  }, [rhf, isLoading, watched]);

  const handleSubmitClick = useCallback(() => {
    rhf.handleSubmit(onSubmit)();
  }, [rhf, onSubmit]);

  const removeErrorMessage = useCallback(() => {
    dispatch(statusReset());
  }, [dispatch]);

  return {
    formik,
    handleSubmitClick,
    statusMessage,
    removeErrorMessage,
  };
};

export default useForgotPasswordForm;
